import { createSwitchlessReducer } from '../../util/switchlessReducer';
import namespace from './namespace';
import type { Dispatch } from 'redux';
import fulfill from '../../fulfill';
import { CORE_API_HOSTNAME, CHECKOUT_API_HOSTNAME } from '../../env';
import { Transaction, Session, OrderItem } from '../../types/checkout';
import { RequestLogsResponse, RequestLog } from '../../types/audit';
import { SwapTransaction, ActionTypes } from '../transactions/actions';
import { createAccountIdChangedCaseReducer } from '../../auth/useAccountId';
import { OperationItem } from '../types';
import { Error } from '../../types/checkout/generated';
import { hasSplitPayment } from '../transactionHelpers';
import { filterEmptySplits } from '../splitPaymentHelpers';

type View = 'details' | 'capture' | 'refund' | 'void' | 'extend';

export interface State {
    isLoading: boolean;
    isNotFound: boolean;
    transaction: Transaction | undefined;
    session: Session | undefined;
    requestLogs: RequestLog[] | undefined;
    details: any;
    error: Error | undefined;
    view: View;
}

export const initialState: State = {
    isLoading: false,
    isNotFound: false,
    transaction: undefined,
    session: undefined,
    requestLogs: undefined,
    details: undefined,
    error: undefined,
    view: 'details',
};

type Requested = { account_id: string; transaction_id: string };
type Received = Requested & { transaction: Transaction };
type Logs = Requested & { logs: RequestLog[] };

export const { reducer, actions } = createSwitchlessReducer({
    namespace: namespace,
    initialState,
    reducers: {
        startLoading: (state: State, { transaction_id }: Requested) => {
            if (transaction_id === state?.transaction?.id) {
                return state;
            }
            return {
                ...state,
                transaction: undefined,
                isLoading: true,
                isNotFound: false,
                requestLogs: undefined,
                details: undefined,
                error: undefined,
                view: 'details' as View,
            };
        },
        setTransaction: (state: State, { transaction }: Received): State => {
            return {
                ...state,
                isLoading: false,
                isNotFound: false,
                transaction,
                session: transaction.session,
            };
        },
        setTransactionNotFound: (state: State) => {
            return {
                ...state,
                isLoading: false,
                isNotFound: true,
            };
        },
        setInitiateOperation: (state: State) => {
            return {
                ...state,
                isLoading: true,
            };
        },
        setUpdatedTransaction: (state: State, { transaction }: {transaction: Transaction}) => {
            return {
                ...state,
                isLoading: false,
                transaction,
                view: 'details' as View,
            };
        },
        setAuditLog: (state: State, { logs }: Logs) => {
            return {
                ...state,
                requestLogs: logs,
            };
        },
        setDetails: (state: State, { details }: {details: any}) => {
            return {
                ...state,
                details,
            };
        },
        setError: (state: State, { error }: {error: Error | undefined}) => {
            return {
                ...state,
                error,
                isLoading: false,
            };
        },
        setView: (state: State, { view }: {view: View}) => {
            return {
                ...state,
                view: view,
            };
        },
    },
    globalReducer: createAccountIdChangedCaseReducer(() => initialState),

});

export const createTransactionActions = (dispatch: Dispatch) => {
    const fetchTransaction = async (account_id: string, transaction_id: string) => {
        dispatch(actions.startLoading({ account_id, transaction_id }));
        const transaction = await fulfill.get({
            accountId: account_id,
            url: `${CHECKOUT_API_HOSTNAME}/v1/transactions/${transaction_id}?includes=events.request_headers&includes=session&includes=initiating_system_request_headers`,
            handlers: {
                200: (transaction: Transaction) => {
                    dispatch(actions.setTransaction({ account_id, transaction_id, transaction }));
                    dispatch(swapTransaction(transaction));
                    return transaction;
                },
                403: () => {
                    dispatch(actions.setTransactionNotFound());
                },
                404: () => {
                    dispatch(actions.setTransactionNotFound());
                },
            },
        }) as Transaction | undefined;
        if (transaction) {
            const getLogs = (entity_id: string): Promise<RequestLog[]> => fulfill.get({
                accountId: account_id,
                url: `${CORE_API_HOSTNAME}/v1/accounts/${account_id}/audit/logs?entity_id=checkout/${entity_id}&limit=100`,
                handlers: {
                    200: (response: RequestLogsResponse) => {
                        return response.items;
                    },
                    404: () => [],
                    403: () => [],
                },
            });
            const logResults = await Promise.all([getLogs(transaction_id), getLogs(transaction.session_id || '')]);
            const sortedLogs = logResults.flat().sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
            dispatch(actions.setAuditLog({ account_id, transaction_id, logs: sortedLogs }));
        }

    };

    const openHistoryDetailsModal = (details: any) => {
        dispatch(actions.setDetails({ details }));
    };

    const closeHistoryDetailsModal = () => {
        dispatch(actions.setDetails({ details: undefined }));
    };

    interface CaptureRefundTransactionRequestBody {
        amount: number;
        items?: OrderItem[];
    }

    const getCaptureRefundRequestBody = (transaction: Transaction, amount: number, items: OperationItem[]) => {
        const requestBody: CaptureRefundTransactionRequestBody = { amount };
        const isSplitPayment = hasSplitPayment(transaction);
        if (items.length > 0 || transaction.payment_product === 'instabank') {
            requestBody.items = items.map(operationItem => {
                return {
                    line_id: operationItem.persisted.orderItem.line_id,
                    id: operationItem.persisted.orderItem.id,
                    quantity: operationItem.operation.quantity,
                    amount: operationItem.operation.amount,
                    splits: isSplitPayment ? filterEmptySplits(operationItem.operation.splits || []) : undefined,
                    fee_split: isSplitPayment ? operationItem.operation.feeSplit : undefined,
                };
            });
        }
        return requestBody;
    };

    const transactionOperationHandlers = {
        200: (t: Transaction) => {
            dispatch(actions.setUpdatedTransaction({  transaction: t }));
            return t;
        },
        202: (t: Transaction) => {
            dispatch(actions.setUpdatedTransaction({ transaction: t }));
            return t;
        },
        400: (error: Error) => {
            dispatch(actions.setError({ error }));
            return;
        },
        404: (error: Error) => {
            dispatch(actions.setError({ error }));
            return;
        },
        403: (error: Error) => {
            dispatch(actions.setError({ error }));
            return;
        },
    };

    const captureTransaction = async (accountId: string, transaction: Transaction, amount: number, items: OperationItem[]) => {
        dispatch(actions.setInitiateOperation());
        const requestBody = getCaptureRefundRequestBody(transaction, amount, items);

        return await fulfill.post<CaptureRefundTransactionRequestBody>({
            url: `${CHECKOUT_API_HOSTNAME}/v1/transactions/${transaction.id}/capture?includes=events.request_headers&includes=initiating_system_request_headers`,
            accountId: accountId,
            json: requestBody,
            handlers: transactionOperationHandlers,
        }) as Transaction | undefined;
    };

    const refundTransaction = async (accountId: string, transaction: Transaction, amount: number, items: OperationItem[]) => {
        dispatch(actions.setInitiateOperation());
        const requestBody = getCaptureRefundRequestBody(transaction, amount, items);
        return await fulfill.post<CaptureRefundTransactionRequestBody>({
            url: `${CHECKOUT_API_HOSTNAME}/v1/transactions/${transaction.id}/refund?includes=events.request_headers&includes=initiating_system_request_headers`,
            accountId: accountId,
            json: requestBody,
            handlers: transactionOperationHandlers,
        }) as Transaction | undefined;
    };

    const voidTransaction = async (accountId: string, transaction: Transaction) => {
        dispatch(actions.setInitiateOperation());
        return await fulfill.post({
            url: `${CHECKOUT_API_HOSTNAME}/v1/transactions/${transaction.id}/void?includes=events.request_headers&includes=initiating_system_request_headers`,
            accountId: accountId,
            handlers: transactionOperationHandlers,
        }) as Transaction | undefined;
    };

    const extendTransaction = async (accountId: string, transaction: Transaction) => {
        dispatch(actions.setInitiateOperation());
        return await fulfill.post({
            url: `${CHECKOUT_API_HOSTNAME}/v1/transactions/${transaction.id}/authorization`,
            accountId: accountId,
            handlers: transactionOperationHandlers,
        }) as Transaction | undefined;
    };

    const setView = (view: View) => {
        dispatch(actions.setView({ view }));
    };

    const closeErrorModal = () => {
        dispatch(actions.setError({ error: undefined }));
    };

    return {
        fetchTransaction,
        openHistoryDetailsModal,
        closeHistoryDetailsModal,
        captureTransaction,
        refundTransaction,
        voidTransaction,
        extendTransaction,
        setView,
        closeErrorModal,
    };
};

/* Legacy side effect support to replace the transaction in the list with the most recent response from checkout-service */
const swapTransaction = (transaction: Transaction): SwapTransaction => ({
    type: ActionTypes.SwapTransaction,
    payload: {
        transaction: transaction,
    },
});
